import useParagonIntegrations from './Paragon/useParagonIntegrations';
import { useSelector } from 'App/components/hooks';

import type {
	ExternalIntegrationHook,
	ExternalIntegration,
	ConnectionMetadata,
	Widget,
	NewWidget
} from './externalIntegrationTypes';
import { makeCancelable } from '@upsales/components/Utils/CancelablePromise';
import ExternalIntegrationWidgetResource from 'App/resources/ExternalIntegrationWidget';

const useExternalIntegrations = (integrationMode: 'paragon' = 'paragon'): ExternalIntegrationHook => {
	const paragonIntegrations = useParagonIntegrations();
	const { availableIntegrations, connectedIntegrations } = useSelector(state => state.ExternalIntegration);

	const getWidgets = async (integration: ExternalIntegration) => {
		const res = await ExternalIntegrationWidgetResource.find({ externalId: integration.type });
		return res.data;
	};

	const getWidgetsByType = async (type: string) => {
		const widgets = await ExternalIntegrationWidgetResource.findByType(type);
		return widgets;
	};

	const saveWidget = async (widget: NewWidget | Widget) => {
		const res = await ExternalIntegrationWidgetResource.save(widget);
		return res.data;
	};

	const deleteWidget = async (widgetId: number) => {
		await ExternalIntegrationWidgetResource.delete(widgetId);
	};

	if (integrationMode === 'paragon') {
		return {
			...paragonIntegrations,
			availableIntegrations,
			connectedIntegrations,
			allIntegrations: [...connectedIntegrations, ...availableIntegrations],
			getWidgets,
			getWidgetsByType,
			saveWidget,
			deleteWidget
		};
	}

	return {
		connectedIntegrations: [],
		availableIntegrations: [],
		allIntegrations: [],
		openIntegrationConfiguration: () => Promise.resolve(),
		installIntegration: (integration: ExternalIntegration) =>
			Promise.resolve({
				updatedIntegration: integration,
				configurationId: undefined,
				installed: false
			}),
		uninstallIntegration: () => Promise.resolve(),
		disconnectConnection: (integration: ExternalIntegration) =>
			Promise.resolve({
				updatedIntegration: integration,
				configurationId: undefined,
				installed: false
			}),
		fetchIntegrations: () =>
			makeCancelable(
				Promise.resolve({
					availableIntegrations: [],
					connectedIntegrations: []
				})
			),
		openIntegration: () => Promise.resolve(),
		updateConnection: (
			integration: ExternalIntegration,
			connectionMetadata: ConnectionMetadata,
			connectionId: string
		) =>
			Promise.resolve({
				updatedIntegration: integration,
				configurationId: undefined,
				installed: false
			}),
		saveGlobalSettings: () => Promise.resolve(),
		saveWorkflowErrorHandlings: integration => Promise.resolve({ updatedIntegration: integration }),
		getFields: () => Promise.resolve([]),
		changeFieldValue: () => Promise.resolve(),
		changeFieldValues: () => Promise.resolve(),
		updateWorkflowEnabled: (integration: ExternalIntegration) =>
			Promise.resolve({ updatedIntegration: integration }),
		getDataSource: () => Promise.resolve({}),
		triggerSync: () => Promise.resolve(),
		getWorkflowFields: () => Promise.resolve([]),
		changeWorkflowFieldValue: () => Promise.resolve(),
		getWorkflowSettings: () => ({ enabled: false, settings: {} }),
		testDataSource: () => Promise.resolve(null),
		isTag: () => false,
		getFieldsWithValues: () => Promise.resolve({}),
		getWidgets,
		getWidgetsByType,
		saveWidget,
		deleteWidget
	};
};

export default useExternalIntegrations;
